import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { InfoPopover } from "@/components/cinch/InfoPopover";
import { KpiTile, tonePct } from "@/components/cinch/KpiTile";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { RetentionResponse } from "../../api";
import { fmtCur, fmtPct, monthLabel } from "../../format";
import { STATUS_MIX_COLOR, STATUS_MIX_FALLBACK } from "./colors";

/** Customer + ARR retention on accounts whose last anniversary already passed. */
export function ActualRetention({ data }: { data: RetentionResponse }) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold">Actual Retention</h2>
        <InfoPopover title="Actual Retention — How it's calculated">
          <p>Every account whose computed renewal month is <code>≤ today</code> is
            included.</p>
          <p><b>Retained</b> = currently <code>Active</code> or <code>At Risk</code> billing status.</p>
          <p><b>Lost</b> = <code>Midterm Churn</code> or <code>Churned</code>.</p>
          <p><b>Customer Retention %</b> = retained ÷ (retained + lost).</p>
          <p><b>ARR Retention %</b> = the same ratio weighted by each account's effective ARR.</p>
        </InfoPopover>
      </div>
      <p className="-mt-3 text-xs text-muted-foreground">
        Renewal months ≤ today · billing status as of now
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <KpiTile
          label="Customer Retention"
          value={fmtPct(data.customer_retention_pct)}
          tone={tonePct(data.customer_retention_pct, 80, 60)}
          sub={`${data.retained_count} of ${data.accounts_in_window} retained · ${data.lost_count} lost`}
          infoTitle="Customer Retention %"
          info={<p>retained / (retained + lost) across the {data.accounts_in_window} accounts whose renewal month has already passed.</p>}
        />
        <KpiTile
          label="ARR Retention"
          value={fmtPct(data.arr_retention_pct)}
          tone={tonePct(data.arr_retention_pct, 80, 60)}
          sub={`${fmtCur(data.retained_arr)} retained · ${fmtCur(data.lost_arr)} lost`}
          infoTitle="ARR Retention %"
          info={<p>Same numerator/denominator as customer retention, but each account weighted by its effective ARR (current MRR × 12; falls back to last-billed MRR × 12 when current is $0).</p>}
        />
        <KpiTile
          label="Accounts in window"
          value={data.accounts_in_window}
          sub="renewal months ≤ current month"
          infoTitle="Accounts in window"
          info={<p>Number of pipeline accounts whose effective renewal date (override → anchor → billing) is on or before the current month.</p>}
        />
        <KpiTile
          label="ARR at stake"
          value={fmtCur(data.arr_at_stake)}
          sub={`${fmtCur(data.retained_arr)} active · ${fmtCur(data.lost_arr)} gone`}
          infoTitle="ARR at stake"
          info={<p>Sum of effective ARR for every in-window account, regardless of whether it was retained or lost.</p>}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Retention by month</CardTitle>
          </CardHeader>
          <CardContent>
            <RetentionBars items={data.by_month} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Status mix</CardTitle>
          </CardHeader>
          <CardContent>
            <StatusDonut items={data.status_mix} />
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function RetentionBars({ items }: { items: RetentionResponse["by_month"] }) {
  if (!items?.length) {
    return (
      <p className="py-4 text-center text-xs text-muted-foreground">
        No past renewals in window.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {items.map((b) => {
        const total = b.retained + b.lost;
        const pct = total > 0 ? (b.retained / total) * 100 : 0;
        const tone = pct >= 80 ? "bg-success" : pct >= 60 ? "bg-warning" : "bg-danger";
        return (
          <div key={b.month} className="grid grid-cols-[6rem_1fr_7rem] items-center gap-2 text-xs">
            <span className="text-muted-foreground">{monthLabel(b.month + "-01")}</span>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-right tabular-nums">
              {fmtPct(pct)}{" "}
              <span className="text-muted-foreground">({b.retained}/{total})</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function StatusDonut({ items }: { items: RetentionResponse["status_mix"] }) {
  const data = (items || []).map((d) => ({
    name: d.status,
    value: d.count,
    fill: STATUS_MIX_COLOR[d.status] ?? STATUS_MIX_FALLBACK,
  }));
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) {
    return (
      <p className="py-4 text-center text-xs text-muted-foreground">
        No accounts in pipeline.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="h-44">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={48}
              outerRadius={72}
              strokeWidth={2}
            >
              {data.map((e, i) => (
                <Cell key={i} fill={e.fill} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => {
                const v = Number(value);
                return [
                  `${v} (${total > 0 ? Math.round((v / total) * 100) : 0}%)`,
                  String(name),
                ];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-col gap-1 text-xs text-muted-foreground">
        {data.map((d) => (
          <div key={d.name} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
              style={{ background: d.fill }}
            />
            <span>
              {d.name}{" "}
              <span className="text-muted-foreground/70">
                {d.value} ({total > 0 ? Math.round((d.value / total) * 100) : 0}%)
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
